/**
 * File: capabilityMappingService.ts
 * Description: Service for IT4IT Capability Mapping operations
 */

import { AsyncJobStartResponse } from '../types/asyncJob';
import {
  CapabilityHierarchyLevel,
  CapabilityMappingFilters,
  CapabilityMappingResponse,
  CapabilityMappingSelectionRequest,
  CapabilityMappingSelectionResponse,
  CapabilityMappingSelectionsExportResponse,
} from '../types/capabilityMapping';

import { apiClient } from './apiClient';
import { startAndPollJob } from './jobPollingService';

export class CapabilityMappingService {
  /**
   * Get distinct portfolio short names for the dropdown selector
   * 
   * @param projectId - The project ID
   * @returns Promise with sorted list of portfolio names
   */
  public async getPortfolios(
    projectId: number,
  ): Promise<string[]> {
    return apiClient.get<string[]>(
      `/api/project/${projectId}/context/capability_mapping/portfolios`,
    );
  }

  /**
   * Get distinct L2 capability names for the dropdown selector
   *
   * @param projectId - The project ID
   * @returns Promise with sorted list of capability names
   */
  public async getCapabilities(
    projectId: number,
  ): Promise<string[]> {
    return apiClient.get<string[]>(
      `/api/project/${projectId}/context/capability_mapping/capabilities`,
    );
  }

  /**
   * Get the nested L1/L2/L3 capability hierarchy for tree filters.
   *
   * @param projectId - The project ID
   * @returns Promise with the hierarchy tree
   */
  public async getCapabilityHierarchy(
    projectId: number,
  ): Promise<CapabilityHierarchyLevel[]> {
    return apiClient.get<CapabilityHierarchyLevel[]>(
      `/api/project/${projectId}/context/capability_mapping/hierarchy`,
    );
  }

  /**
   * Get distinct L3 subcategory names for the dropdown selector
   *
   * @param projectId - The project ID
   * @param capabilities - Optional capability filter
   * @returns Promise with sorted list of subcategory names
   */
  public async getSubcategories(
    projectId: number,
    capabilities?: string[],
  ): Promise<string[]> {
    const query = new URLSearchParams();
    capabilities?.forEach((capability) => {
      query.append('capabilities', capability);
    });
    const params = query.toString();
    const endpoint = params
      ? `/api/project/${projectId}/context/capability_mapping/subcategories?${params}`
      : `/api/project/${projectId}/context/capability_mapping/subcategories`;

    return apiClient.get<string[]>(endpoint);
  }

  /**
   * Generate capability mapping for a project
   * Runs the IT4IT agent to analyze BRD and map to capabilities/applications.
   *
   * The work runs as a background job to avoid platform request timeouts:
   * it starts the job, then polls until the mapping result is ready.
   *
   * @param projectId - The project ID to analyze
   * @param filters - Optional portfolio/capability/subcategory filters
   * @returns Promise with capability mapping results
   * @throws ApiError on request failure
   */
  public async generateCapabilityMapping(
    projectId: number,
    filters?: CapabilityMappingFilters,
  ): Promise<CapabilityMappingResponse> {
    const body: CapabilityMappingFilters = {};

    if (filters?.portfolios?.length) {
      body.portfolios = filters.portfolios;
    }
    if (filters?.capabilities?.length) {
      body.capabilities = filters.capabilities;
    }
    if (filters?.subcategories?.length) {
      body.subcategories = filters.subcategories;
    }

    const requestBody = Object.keys(body).length > 0 ? body : undefined;

    return startAndPollJob<CapabilityMappingResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/project/${projectId}/context/capability_mapping/generate/async`,
          requestBody,
        ),
    );
  }

  /**
   * Get all capability mapping selections for a project
   * 
   * @param projectId - The project ID
   * @param status - Optional filter by status ("accepted" or "skipped")
   * @returns Promise with list of selections
   */
  public async getSelections(
    projectId: number,
    status?: string,
  ): Promise<CapabilityMappingSelectionResponse[]> {
    const params = status ? `?status=${status}` : '';
    return apiClient.get<CapabilityMappingSelectionResponse[]>(
      `/api/project/${projectId}/context/capability_mapping/selections${params}`,
    );
  }

  /**
   * Accept or skip an application for a requirement
   * 
   * @param projectId - The project ID
   * @param selection - The selection data
   * @returns Promise with the created/updated selection
   */
  public async addSelection(
    projectId: number,
    selection: CapabilityMappingSelectionRequest,
  ): Promise<CapabilityMappingSelectionResponse> {
    return apiClient.post<CapabilityMappingSelectionResponse>(
      `/api/project/${projectId}/context/capability_mapping/selections`,
      selection,
    );
  }

  /**
   * Delete a capability mapping selection
   * 
   * @param projectId - The project ID
   * @param selectionId - The selection ID to delete
   */
  public async deleteSelection(
    projectId: number,
    selectionId: number,
  ): Promise<void> {
    return apiClient.delete(
      `/api/project/${projectId}/context/capability_mapping/selections/${selectionId}`,
    );
  }

  /**
   * Delete all capability mapping selections for a project
   * 
   * @param projectId - The project ID
   * @returns Promise with count of deleted selections
   */
  public async deleteAllSelections(
    projectId: number,
  ): Promise<{ deleted_count: number }> {
    return apiClient.delete<{ deleted_count: number }>(
      `/api/project/${projectId}/context/capability_mapping/selections`,
    );
  }

  /**
   * Export capability mapping selections as a markdown file
   * Creates a formatted markdown document and uploads to project context
   * 
   * @param projectId - The project ID
   * @returns Promise with export result including file info
   */
  public async exportSelectionsAsMarkdown(
    projectId: number,
  ): Promise<CapabilityMappingSelectionsExportResponse> {
    return apiClient.post<CapabilityMappingSelectionsExportResponse>(
      `/api/project/${projectId}/context/capability_mapping/selections/export`,
    );
  }
}

export const capabilityMappingService = new CapabilityMappingService();

